import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useWebsiteProductsQuery,
  useProductVariantsQuery,
  useCreateVariantMutation,
  useSaveVariantMutation,
  useDeleteVariantMutation
} from "@queries/website";
import "@styles/opencart-admin.css";

const defaultForm = {
  name: "",
  name_ar: "",
  subtitle: "",
  subtitle_ar: "",
  price: 0,
  special: "",
  sort_order: 0,
  status: true
};

export default function ProductVariantsPage() {
  const [searchParams] = useSearchParams();
  // Deep-link support: /website/product-variants?product_id=4018 opens straight
  // on that product's sizes/prices (e.g. linked from the catalog product page).
  const initialId = Number(searchParams.get("product_id")) || null;
  const [productId, setProductId] = React.useState(initialId);
  const [manualId, setManualId] = React.useState("");

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);
  const [validationErrors, setValidationErrors] = React.useState({});

  const queryClient = useQueryClient();

  const { data: products, isLoading: productsLoading } = useWebsiteProductsQuery();
  const { data: variants, isFetching: variantsFetching } = useProductVariantsQuery(productId);

  const createMutation = useCreateVariantMutation();
  const saveMutation = useSaveVariantMutation();
  const deleteMutation = useDeleteVariantMutation();

  React.useEffect(() => {
    if (!productId && products?.length) {
      setProductId(products[0].product_id);
    }
  }, [products, productId]);

  const selectedProduct = (products || []).find((p) => p.product_id === productId);

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
    setValidationErrors({});
  }

  function openEdit(v) {
    setEditingId(v.variant_id);
    setValidationErrors({});
    setForm({
      name: v.name || "",
      name_ar: v.name_ar || "",
      subtitle: v.subtitle || "",
      subtitle_ar: v.subtitle_ar || "",
      price: v.price ?? 0,
      special: v.special ?? "",
      sort_order: v.sort_order ?? 0,
      status: !!v.status
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setValidationErrors({});

    const payload = {
      name: form.name,
      name_ar: form.name_ar || null,
      subtitle: form.subtitle || null,
      subtitle_ar: form.subtitle_ar || null,
      price: Number(form.price) || 0,
      special: form.special === "" || form.special == null ? null : Number(form.special),
      sort_order: Number(form.sort_order) || 0,
      status: form.status ? 1 : 0
    };

    try {
      if (editingId) {
        await saveMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createMutation.mutateAsync({ productId, payload });
      }
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["website", "variants", productId] });
      queryClient.invalidateQueries({ queryKey: ["website", "products"] });
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      } else {
        alert(err?.response?.data?.message || "Failed to save variant");
      }
    }
  }

  async function handleDelete(v) {
    if (!window.confirm(`Delete variant "${v.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(v.variant_id);
      queryClient.invalidateQueries({ queryKey: ["website", "variants", productId] });
      queryClient.invalidateQueries({ queryKey: ["website", "products"] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete variant");
    }
  }

  const fieldError = (key) =>
    validationErrors[key] && (
      <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>{validationErrors[key][0]}</div>
    );

  return (
    <div className="oc-content">
      {(productsLoading || variantsFetching) && <Loader />}

      <div className="oc-header">
        <h1>Shop Product Variants</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Product Variants</span>
        </nav>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16 }}>
        <div className="oc-card" style={{ margin: 0 }}>
          <div className="title">Shop Products</div>
          <table className="oc-table">
            <tbody>
              {(products || []).map((p) => (
                <tr
                  key={p.product_id}
                  onClick={() => {
                    setProductId(p.product_id);
                    resetForm();
                  }}
                  style={{
                    cursor: "pointer",
                    background: p.product_id === productId ? "#eef4ff" : undefined
                  }}
                >
                  <td>
                    {p.name || `Product #${p.product_id}`}
                    <div style={{ fontSize: 12, color: "#777" }}>
                      #{p.product_id} · {p.variant_count} variant{p.variant_count === 1 ? "" : "s"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Open product by ID</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="number"
                className="oc-input"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="product_id"
              />
              <button
                className="oc-btn ghost"
                onClick={() => {
                  if (manualId) {
                    setProductId(Number(manualId));
                    resetForm();
                  }
                }}
              >
                Open
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
              Use this to add variants to a shop product that has none yet.
            </div>
          </div>
        </div>

        <div>
          <div className="oc-card" style={{ margin: 0 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <div>
                <strong>
                  {selectedProduct?.name || (productId ? `Product #${productId}` : "Pick a product")}
                </strong>
                {selectedProduct && (
                  <div style={{ fontSize: 12, color: "#777" }}>
                    base price {Number(selectedProduct.price).toFixed(2)} ·{" "}
                    {selectedProduct.status ? "enabled" : "disabled"}
                  </div>
                )}
              </div>
            </div>

            <table className="oc-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>ID</th>
                  <th>Name</th>
                  <th>Subtitle</th>
                  <th style={{ width: 90 }}>Price</th>
                  <th style={{ width: 90 }}>Special</th>
                  <th style={{ width: 70 }}>Sort</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(variants || []).length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: 24 }}>
                      No variants for this product
                    </td>
                  </tr>
                )}
                {(variants || []).map((v) => (
                  <tr key={v.variant_id}>
                    <td>{v.variant_id}</td>
                    <td>
                      {v.name}
                      {v.name_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {v.name_ar}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {v.subtitle}
                      {v.subtitle_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {v.subtitle_ar}
                        </div>
                      ) : null}
                    </td>
                    <td>{Number(v.price).toFixed(2)}</td>
                    <td>{v.special != null ? Number(v.special).toFixed(2) : "—"}</td>
                    <td>{v.sort_order}</td>
                    <td>{v.status ? "Enabled" : "Disabled"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="oc-btn blue" onClick={() => openEdit(v)}>
                          edit
                        </button>
                        <button className="oc-btn danger" onClick={() => handleDelete(v)}>
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {productId && (
            <div className="oc-card" style={{ marginTop: 16 }}>
              <div className="oc-toolbar" style={{ marginBottom: 12 }}>
                <div>
                  <strong>{editingId ? `Edit Variant #${editingId}` : "Add Variant"}</strong>
                </div>
                <div>
                  <button className="oc-btn ghost" onClick={resetForm}>
                    New
                  </button>
                </div>
              </div>

              <form onSubmit={handleSave}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name (English)</label>
                    <input
                      className="oc-input"
                      placeholder="e.g. 5 L · 125 washes"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      required
                    />
                    {fieldError("name")}
                  </div>
                  <div className="form-group">
                    <label>Name (Arabic)</label>
                    <input
                      className="oc-input"
                      dir="rtl"
                      placeholder="مثال: 5 لتر · 125 غسلة"
                      value={form.name_ar}
                      onChange={(e) => setForm((f) => ({ ...f, name_ar: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Subtitle (English)</label>
                    <input
                      className="oc-input"
                      placeholder="e.g. JOD 0.26 per wash"
                      value={form.subtitle}
                      onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle (Arabic)</label>
                    <input
                      className="oc-input"
                      dir="rtl"
                      placeholder="مثال: 0.26 دينار لكل غسلة"
                      value={form.subtitle_ar}
                      onChange={(e) => setForm((f) => ({ ...f, subtitle_ar: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price (JOD)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="oc-input"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      required
                    />
                    {fieldError("price")}
                  </div>
                  <div className="form-group">
                    <label>Special Price (optional)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="oc-input"
                      value={form.special}
                      onChange={(e) => setForm((f) => ({ ...f, special: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Sort Order</label>
                    <input
                      type="number"
                      className="oc-input"
                      value={form.sort_order}
                      onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                      <label>
                        <input
                          type="radio"
                          name="variant-status"
                          checked={form.status === true}
                          onChange={() => setForm((f) => ({ ...f, status: true }))}
                        />{" "}
                        Enabled
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="variant-status"
                          checked={form.status === false}
                          onChange={() => setForm((f) => ({ ...f, status: false }))}
                        />{" "}
                        Disabled
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" className="oc-btn ghost" onClick={resetForm}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="oc-btn primary"
                    disabled={createMutation.isPending || saveMutation.isPending}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
